import React, { useMemo, useState } from "react";
import {
  Atom, Blocks, BookOpen, Bot, Braces, Brain, Calculator, Camera, ChartLine, ChevronDown, Clapperboard, Cloud, Code,
  Cpu, Database, Dna, FlaskConical, Gamepad2, Globe, GraduationCap, Languages, Leaf, Lightbulb, Map, Microscope,
  Music, Palette, Pencil, PenTool, Puzzle, Rocket, Search, Shapes, ShieldCheck, Smartphone, Sparkles, Sigma, Star,
  Telescope, Terminal, Trophy, Wrench,
} from "lucide-react";
import { Col, Dropdown, DropdownMenu, DropdownToggle, Input, Label, Row } from "reactstrap";

/**
 * Icons an admin can pick for a path. Keys must match the API (models/model.learning_path.js:
 * LEARNING_PATH_ICONS) and the AI app (src/features/learning-pathways/components/icons.ts:
 * PATHWAY_ICONS), which draws the same lucide-react icon for each key. Add new icons in all three.
 */
export const LEARNING_PATH_ICONS = [
  { value: "python", label: "Python", group: "Coding & tech", Icon: Terminal },
  { value: "web", label: "Web", group: "Coding & tech", Icon: Globe },
  { value: "code", label: "Code", group: "Coding & tech", Icon: Code },
  { value: "braces", label: "Brackets", group: "Coding & tech", Icon: Braces },
  { value: "database", label: "Data", group: "Coding & tech", Icon: Database },
  { value: "cpu", label: "Hardware", group: "Coding & tech", Icon: Cpu },
  { value: "cloud", label: "Cloud", group: "Coding & tech", Icon: Cloud },
  { value: "smartphone", label: "Apps", group: "Coding & tech", Icon: Smartphone },
  { value: "shield", label: "Cyber safety", group: "Coding & tech", Icon: ShieldCheck },
  { value: "blocks", label: "Blocks", group: "Coding & tech", Icon: Blocks },
  { value: "wrench", label: "Build", group: "Coding & tech", Icon: Wrench },
  { value: "ai", label: "AI", group: "AI", Icon: Bot },
  { value: "brain", label: "Brain", group: "AI", Icon: Brain },
  { value: "sparkles", label: "Sparkles", group: "AI", Icon: Sparkles },
  { value: "game", label: "Games", group: "Creative & games", Icon: Gamepad2 },
  { value: "puzzle", label: "Puzzles", group: "Creative & games", Icon: Puzzle },
  { value: "palette", label: "Art", group: "Creative & games", Icon: Palette },
  { value: "music", label: "Music", group: "Creative & games", Icon: Music },
  { value: "camera", label: "Photography", group: "Creative & games", Icon: Camera },
  { value: "film", label: "Film", group: "Creative & games", Icon: Clapperboard },
  { value: "pen", label: "Design", group: "Creative & games", Icon: PenTool },
  { value: "shapes", label: "Shapes", group: "Creative & games", Icon: Shapes },
  { value: "pencil", label: "Writing", group: "Creative & games", Icon: Pencil },
  { value: "calculator", label: "Maths", group: "Maths & science", Icon: Calculator },
  { value: "sigma", label: "Algebra", group: "Maths & science", Icon: Sigma },
  { value: "chart", label: "Charts", group: "Maths & science", Icon: ChartLine },
  { value: "atom", label: "Science", group: "Maths & science", Icon: Atom },
  { value: "flask", label: "Chemistry", group: "Maths & science", Icon: FlaskConical },
  { value: "microscope", label: "Biology", group: "Maths & science", Icon: Microscope },
  { value: "dna", label: "Genetics", group: "Maths & science", Icon: Dna },
  { value: "telescope", label: "Space", group: "Maths & science", Icon: Telescope },
  { value: "leaf", label: "Nature", group: "Maths & science", Icon: Leaf },
  { value: "map", label: "Geography", group: "Maths & science", Icon: Map },
  { value: "book", label: "Reading", group: "General", Icon: BookOpen },
  { value: "languages", label: "Languages", group: "General", Icon: Languages },
  { value: "lightbulb", label: "Ideas", group: "General", Icon: Lightbulb },
  { value: "rocket", label: "Rocket", group: "General", Icon: Rocket },
  { value: "trophy", label: "Challenge", group: "General", Icon: Trophy },
  { value: "graduation", label: "Graduation", group: "General", Icon: GraduationCap },
  { value: "star", label: "Star", group: "General", Icon: Star },
];
const ICON_GROUPS = [...new Set(LEARNING_PATH_ICONS.map((i) => i.group))];

/** Every path uses the app's primary colour (same plum as the learning app). */
export const LEARNING_PATH_COLOR = "#AA468E";

export const LEARNING_PATH_LEVELS = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

export const emptyLearningPathMeta = () => ({
  title: "",
  icon: "python",
  sortOrder: 0,
  level: "",
  subcategoryId: "",
});

const iconFor = (value) => LEARNING_PATH_ICONS.find((i) => i.value === value) || LEARNING_PATH_ICONS[0];

/** The path's icon as students see it, in the path colour. */
export const LearningPathIcon = ({ icon, size = 18 }) => {
  const match = iconFor(icon);
  const hex = LEARNING_PATH_COLOR;
  const { Icon } = match;
  return (
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{ width: size + 16, height: size + 16, background: `${hex}1F`, color: hex }}
      title={match.label}
      aria-label={`${match.label} icon`}
      role="img"
    >
      <Icon size={size} aria-hidden />
    </span>
  );
};

/** Button showing the chosen icon; opens a searchable grid of every available icon. */
export const LearningPathIconPicker = ({ value, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const current = iconFor(value);
  const hex = LEARNING_PATH_COLOR;
  const CurrentIcon = current.Icon;

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = LEARNING_PATH_ICONS.filter(
      (i) => !q || i.label.toLowerCase().includes(q) || i.value.includes(q) || i.group.toLowerCase().includes(q)
    );
    return ICON_GROUPS.map((group) => ({ group, icons: matches.filter((i) => i.group === group) })).filter((g) => g.icons.length);
  }, [query]);

  const toggle = () => {
    if (disabled) return;
    setOpen((o) => !o);
    setQuery("");
  };

  return (
    <Dropdown isOpen={open} toggle={toggle} className="w-100">
      <DropdownToggle
        tag="button"
        type="button"
        disabled={disabled}
        aria-label={`Icon: ${current.label}. Change icon`}
        className="form-select d-flex align-items-center gap-2 text-start"
        style={{ backgroundImage: "none" }}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
          style={{ width: 28, height: 28, background: `${hex}1F`, color: hex }}
        >
          <CurrentIcon size={16} aria-hidden />
        </span>
        <span className="flex-grow-1 text-truncate">{current.label}</span>
        <ChevronDown size={16} className="text-muted" aria-hidden />
      </DropdownToggle>
      <DropdownMenu className="p-2 shadow" style={{ width: 340, maxWidth: "90vw" }}>
        <div className="position-relative mb-2">
          <Search size={14} className="position-absolute text-muted" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} aria-hidden />
          <Input
            bsSize="sm"
            autoFocus
            placeholder="Search icons…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ paddingLeft: 30 }}
            aria-label="Search icons"
          />
        </div>
        <div style={{ maxHeight: 300, overflowY: "auto" }} role="radiogroup" aria-label="Icons">
          {groups.length === 0 && <div className="text-muted small text-center py-3">No icons match "{query}".</div>}
          {groups.map(({ group, icons }) => (
            <div key={group} className="mb-2">
              <div className="text-muted text-uppercase fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: "0.06em" }}>{group}</div>
              <div className="d-grid gap-1" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
                {icons.map(({ value: iconValue, label, Icon }) => {
                  const selected = iconValue === value;
                  return (
                    <button
                      key={iconValue}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-label={label}
                      title={label}
                      onClick={() => {
                        onChange(iconValue);
                        setOpen(false);
                      }}
                      className="btn btn-sm d-flex flex-column align-items-center justify-content-center gap-1 py-2 px-1"
                      style={{
                        borderRadius: 10,
                        border: `1px solid ${selected ? hex : "transparent"}`,
                        background: selected ? `${hex}14` : "transparent",
                        color: selected ? hex : "#495057",
                      }}
                    >
                      <Icon size={20} aria-hidden />
                      <span className="text-truncate w-100" style={{ fontSize: 10 }}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

/**
 * Metadata fields shared by the "New learning path" modal and the editor.
 * @param {{ categories?: Array<{ id: number, title: string, subcategories?: Array<{ id: number, title: string }> }> }} props
 */
const LearningPathMetaFields = ({ value, onChange, disabled = false, categories = [] }) => {
  const set = (key) => (e) =>
    onChange({
      ...value,
      [key]:
        key === "sortOrder"
          ? Number(e.target.value || 0)
          : key === "subcategoryId"
            ? (e.target.value === "" ? "" : Number(e.target.value))
            : e.target.value,
    });

  const subcategoryOptions = categories.flatMap((cat) =>
    (cat.subcategories || []).map((sub) => ({
      id: sub.id,
      label: `${cat.title} → ${sub.title}`,
    }))
  );

  return (
    <Row className="g-3">
      <Col md={12}>
        <Label className="form-label">Title *</Label>
        <Input value={value.title} onChange={set("title")} maxLength={191} disabled={disabled} placeholder="e.g. JavaScript Beginner: First Programs" />
      </Col>
      <Col md={8}>
        <Label className="form-label">Subcategory</Label>
        <Input type="select" value={value.subcategoryId ?? ""} onChange={set("subcategoryId")} disabled={disabled || !subcategoryOptions.length}>
          <option value="">{subcategoryOptions.length ? "Not assigned" : "Create a category & subcategory first"}</option>
          {subcategoryOptions.map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </Input>
        <small className="text-muted">Paths sit under a subcategory (e.g. Coding → JavaScript).</small>
      </Col>
      <Col md={4}>
        <Label className="form-label">Level</Label>
        <Input type="select" value={value.level || ""} onChange={set("level")} disabled={disabled}>
          <option value="">Not set</option>
          {LEARNING_PATH_LEVELS.map((lvl) => (
            <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
          ))}
        </Input>
      </Col>
      <Col md={8}>
        <Label className="form-label">Icon</Label>
        <LearningPathIconPicker
          value={value.icon}
          disabled={disabled}
          onChange={(icon) => onChange({ ...value, icon })}
        />
      </Col>
      <Col md={4}>
        <Label className="form-label">Sort order</Label>
        <Input type="number" min={0} value={value.sortOrder ?? 0} onChange={set("sortOrder")} disabled={disabled} />
        <small className="text-muted">Lower numbers show first.</small>
      </Col>
    </Row>
  );
};

export default LearningPathMetaFields;
